import React, { useCallback } from 'react';
import {
  Pane,
  List,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
  Icon,
} from '@fullstory/components';
import { useSelectableList } from '@fullstory/react-hooks';
import { useIntl } from 'react-intl';

export type MultiColumnTimePickerListItem<T> = {
  id: T;
  label: string;
  subText?: string;
  disabled?: boolean;
};

type MultiColumnTimePickerListProps<T> = {
  items: Array<MultiColumnTimePickerListItem<T>>;
  onChange: (id: T) => void;
  isActiveItem: (id: T) => boolean;
  iconColor: React.ComponentPropsWithoutRef<typeof Icon>['color'];
  title?: string;
  testId?: string;
  showCustomTimeRangeWarning?: boolean;
};

export function MultiColumnTimePickerList<T>({
  items,
  onChange,
  isActiveItem,
  iconColor,
  title,
  testId = 'multi-column-time-picker-list',
  showCustomTimeRangeWarning,
}: MultiColumnTimePickerListProps<T>) {
  const intl = useIntl();

  const handleOnChange = useCallback(
    (item: MultiColumnTimePickerListItem<T>) => {
      onChange(item.id);
    },
    [onChange],
  );
  const { getListItemProps } = useSelectableList(
    items,
    (maybeOption, option) => maybeOption.id === option.id,
    handleOnChange,
    // TODO(ralfstory): set active when list is active
    { active: true },
  );
  return (
    <Pane
      data-component="MultiColumnTimePickerList"
      borderRight="standard"
      testId={testId}
      width="max-content"
      minWidth={150}
    >
      <List>
        {title && (
          <MenuItem disabled>
            <ListItemText text={title} inset />
          </MenuItem>
        )}
        {items.map(item => (
          <MenuItem
            key={`multi-select-${item.id}`}
            {...getListItemProps(item)}
            disabled={item.disabled}
          >
            {isActiveItem(item.id) && <ListItemIcon color={iconColor} name="checkmark" />}
            <ListItemText text={item.label} inset={!isActiveItem(item.id)} subtext={item.subText} />
            {showCustomTimeRangeWarning && item.label === 'Custom' && (
              <Tooltip
                tooltipContent={intl.formatMessage({
                  defaultMessage:
                    'When a custom time range is applied, dynamic color values may not appear as intended',
                  id: 'UNGYO5',
                })}
              >
                <Icon name="information" color="secondary" size={16} />
              </Tooltip>
            )}
          </MenuItem>
        ))}
      </List>
    </Pane>
  );
}
